import { useEffect, useState } from 'react';
import { MdBackup, MdCloudDownload } from 'react-icons/md';
import api from '../services/api';
import toast from 'react-hot-toast';
import './DatabaseBackup.css';

const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const parseBackupError = async (err) => {
  if (err.code === 'ECONNABORTED') {
    return 'Backup timed out. Try again or run mongodump manually for very large databases.';
  }
  if (err.response?.data instanceof Blob) {
    try {
      const text = await err.response.data.text();
      const json = JSON.parse(text);
      return json.message || 'Backup failed';
    } catch {
      /* fall through */
    }
  }
  return err.response?.data?.message || err.message || 'Backup failed';
};

const downloadBackupBlob = async (url, fallbackFilename) => {
  const res = await api.get(url, {
    responseType: 'blob',
    timeout: 30 * 60 * 1000,
  });

  const disposition = res.headers['content-disposition'] || '';
  const match = disposition.match(/filename="([^"]+)"/);
  const filename = match?.[1] || fallbackFilename;

  const blob = new Blob([res.data], { type: 'application/zip' });
  triggerBlobDownload(blob, filename);
  return filename;
};

const DatabaseBackup = () => {
  const [loading, setLoading] = useState(false);
  const [clusters, setClusters] = useState([]);
  const [progress, setProgress] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/backup/mongodb/meta');
        if (!cancelled) {
          setClusters(res.data?.clusters || []);
        }
      } catch {
        if (!cancelled) setClusters([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDownloadBackup = async () => {
    const clusterCount = clusters.length || 1;
    const confirmMessage =
      clusterCount > 1
        ? `Create full MongoDB backups for ${clusterCount} clusters? You will download one ZIP file that contains ${clusterCount} separate cluster ZIP files inside. This may take several minutes.`
        : 'Create a full MongoDB backup and download it as a ZIP file? This may take a few minutes for large databases.';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    setProgress('');
    try {
      let meta = { clusters };
      if (!meta.clusters?.length) {
        const res = await api.get('/backup/mongodb/meta');
        meta = res.data;
        setClusters(res.data?.clusters || []);
      }

      const list = meta?.clusters?.length ? meta.clusters : [{ index: 0, label: 'cluster' }];

      if (list.length > 1) {
        setProgress(`Backing up ${list.length} clusters on the server…`);
        const filename = await downloadBackupBlob(
          '/backup/mongodb/bundle',
          `mongodb-backup-all-clusters-${new Date().toISOString().slice(0, 10)}.zip`,
        );
        toast.success(
          `Downloaded ${filename}. Extract it to get ${list.length} separate cluster ZIP files.`,
        );
        return;
      }

      setProgress('Creating backup…');
      await downloadBackupBlob(
        '/backup/mongodb',
        `mongodb-backup-${new Date().toISOString().slice(0, 10)}.zip`,
      );
      toast.success('Backup downloaded to your Downloads folder');
    } catch (err) {
      toast.error(await parseBackupError(err));
    } finally {
      setLoading(false);
      setProgress('');
    }
  };

  return (
    <div className="db-backup-page">
      <div className="db-backup-header">
        <h1>
          <MdBackup size={28} />
          Database Backup
        </h1>
      </div>

      <div className="db-backup-card">
        <div className="db-backup-card-icon">
          <MdCloudDownload size={40} />
        </div>
        <h2>Download MongoDB backup</h2>

        <button
          type="button"
          className="db-backup-btn"
          onClick={handleDownloadBackup}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner db-backup-spinner" />
              {progress || `Creating backup${clusters.length > 1 ? 's' : ''}…`}
            </>
          ) : (
            <>
              <MdCloudDownload size={20} />
              {clusters.length > 1
                ? 'Download all clusters (1 ZIP bundle)'
                : 'Download backup (ZIP)'}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default DatabaseBackup;
